import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Truck, RotateCcw, BadgeCheck, ArrowRight, Smartphone, Tablet, Laptop } from "lucide-react";
import { api } from "../lib/api";
import ApplyButton from "../components/ApplyButton";
import TrustMarquee from "../components/TrustMarquee";
import ProductCard from "../components/ProductCard";
import LeaseCalculator from "../components/LeaseCalculator";
import { Reveal } from "../components/Reveal";
import { isIpad, isMacbook } from "../lib/category";

const heroLines = ["Your next device.", "More affordable!", "Just lease it."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({ y: "0%", transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.15 * i } }),
};

const badges = [
  { icon: BadgeCheck, label: "PhoneCheck Certified" },
  { icon: ShieldCheck, label: "1-Year Warranty" },
  { icon: Truck, label: "Free UPS Shipping" },
  { icon: RotateCcw, label: "30-Day Returns" },
];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const featuredIphones = products.filter((p) => !isIpad(p) && !isMacbook(p));
  const featuredIpads = products.filter(isIpad);
  const featuredMacbooks = products.filter(isMacbook);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-44">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-12">
          <div>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-block rounded-full border border-line bg-alt px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-muted-foreground"
            >
              Financing via the Payvantage program
            </motion.span>
            <h1 className="mt-6 font-display text-5xl font-black leading-[0.95] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
              {heroLines.map((line, i) => (
                <span key={i} className="mask-line">
                  <motion.span
                    className="block"
                    custom={i}
                    variants={lineVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {i === 2 ? (
                      <>Just <span className="text-crimson">lease</span> it.</>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground"
            >
              Get a certified like-new device today and pay over time. Flexible lease-to-own plans with no huge upfront cost — unlocked and carrier-specific options available.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.6 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <ApplyButton />
            </motion.div>
          </div>

          <div className="relative flex justify-center">
            <div className="absolute inset-0 -z-10 mx-auto h-72 w-72 rounded-full bg-crimson/20 blur-3xl" />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="grid w-full max-w-sm gap-5"
            >
              {[
                { to: "/products", label: "Shop iPhones", sub: "Certified like-new iPhones", icon: Smartphone, testid: "hero-shop-iphones" },
                { to: "/ipads", label: "Shop iPads", sub: "WiFi & Cellular models", icon: Tablet, testid: "hero-shop-ipads" },
                { to: "/macbooks", label: "Shop MacBooks", sub: "Certified like-new MacBooks", icon: Laptop, testid: "hero-shop-macbooks" },
              ].map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={c.testid}
                  className="group flex items-center gap-5 rounded-[1.75rem] border border-line bg-white p-6 shadow-xl transition-all hover:-translate-y-1 hover:border-crimson hover:shadow-2xl"
                >
                  <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-alt text-crimson transition-colors group-hover:bg-crimson group-hover:text-white">
                    <c.icon className="h-8 w-8" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-2xl font-black tracking-tight text-ink">{c.label}</div>
                    <div className="text-sm text-muted-foreground">{c.sub}</div>
                  </div>
                  <ArrowRight className="ml-auto h-6 w-6 flex-shrink-0 text-muted-foreground transition-all group-hover:translate-x-1 group-hover:text-crimson" />
                </Link>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <TrustMarquee />

      {/* BADGES */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-12">
        <div className="grid grid-cols-2 gap-6 auto-rows-fr lg:grid-cols-4">
          {badges.map((b, i) => (
            <Reveal key={b.label} delay={i * 0.08} className="h-full">
              <div className="flex h-full flex-col rounded-2xl border border-line p-6" data-testid={`badge-${i}`}>
                <b.icon className="h-8 w-8 text-crimson" />
                <div className="mt-4 font-display text-lg font-bold text-ink">{b.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED iPhones */}
      <section className="bg-alt py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal>
            <div className="flex items-end justify-between">
              <div>
                <span className="text-sm font-bold uppercase tracking-widest text-crimson">Popular right now</span>
              </div>
              <Link to="/products" data-testid="featured-viewall" className="hidden items-center gap-2 text-sm font-bold text-ink hover:text-crimson md:flex">
                View all <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-iphones-grid">
            {featuredIphones.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED iPads */}
      {featuredIpads.length > 0 && (
        <section className="py-24" data-testid="featured-ipads-section">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <Reveal>
              <div className="flex items-end justify-between">
                <div>
                  <span className="text-sm font-bold uppercase tracking-widest text-crimson">Featured iPads</span>
                </div>
                <Link to="/ipads" data-testid="featured-ipads-viewall" className="hidden items-center gap-2 text-sm font-bold text-ink hover:text-crimson md:flex">
                  View all <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-ipads-grid">
              {featuredIpads.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED MacBooks */}
      {featuredMacbooks.length > 0 && (
        <section className="bg-alt py-24" data-testid="featured-macbooks-section">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <Reveal>
              <div className="flex items-end justify-between">
                <div>
                  <span className="text-sm font-bold uppercase tracking-widest text-crimson">Featured MacBooks</span>
                </div>
                <Link to="/macbooks" data-testid="featured-macbooks-viewall" className="hidden items-center gap-2 text-sm font-bold text-ink hover:text-crimson md:flex">
                  View all <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-macbooks-grid">
              {featuredMacbooks.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <LeaseCalculator />

      {/* CTA */}
      <section className="bg-surface-dark py-28 text-white">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-12">
          <Reveal>
            <h2 className="font-display text-4xl font-black tracking-tighter sm:text-5xl">
              Approved in minutes. <span className="text-crimson">Checking won't affect your credit.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-lg text-white/60">
              Apply through the Payvantage leasing program and walk away with the iPhone you want today.
            </p>
            <div className="mt-8 flex justify-center">
              <ApplyButton testId="cta-apply-btn" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
